<script setup lang="ts">
import type { VTabItemProps } from "./VTabItem.types";
import { computed, inject, onBeforeUnmount, onMounted, reactive, ref, toRef, toRefs, watch } from "vue";
import VIcon from "../../Icon/VIcon.vue";
import { TAB_BAR_OVERFLOW, TAB_LIST_BAR } from "../tabBar";
import { useTabsState } from "../useTabsState";
import { useProvideTabItemState } from "./useTabItemState";
import { headerTabItemCva, skeletonTabItemCva, tabCountCva, tabIconCva, tabItemCva, tabTitleCva } from "./VTabItem.cva";

const props = withDefaults(defineProps<VTabItemProps>(), {
  disabled: false,
  icon: undefined,
  tooltip: undefined,
  tooltipPosition: "right",
  title: "",
  error: false,
  skeleton: false,
  rounded: false,
  badge: undefined,
  count: undefined,
  muted: false,
  tone: "default",
  locked: false,
});

const isBar = inject(TAB_LIST_BAR, ref(false));

const { disabled, error, index, rounded } = toRefs(props);

const {
  activeIndex,
  tabItems,
  registerTab,
  unregisterTab,
  onTabClick,
  orientation,
  tile,
  listPosition,
  verticalTileWidth,
  appearance,
} = useTabsState();

const isHeader = computed(() => appearance?.value === "header" && orientation?.value === "horizontal" && !tile?.value);

const barButton = ref<HTMLElement | null>(null);
const barOverflow = inject(TAB_BAR_OVERFLOW, ref(new Set<number>()));

onMounted(() => {
  registerTab(reactive({
    index: index.value,
    error: error.value,
    disabled: disabled.value,
    danger: computed(() => props.tone === "danger"),
    title: toRef(props, "title"),
    icon: toRef(props, "icon"),
    el: barButton,
  }));
});

onBeforeUnmount(() => {
  unregisterTab(index.value);
});

watch([error, disabled], ([newError, newDisabled]) => {
  const existingTab = tabItems.value.find(tab => tab.index === index.value);
  if (existingTab) {
    existingTab.error = newError;
    existingTab.disabled = newDisabled;
  }
});

useProvideTabItemState({
  error,
  disabled,
  index,
});

function handleClick() {
  if (!disabled.value) {
    onTabClick(index.value);
  }
}

function handleKeyDown(event: KeyboardEvent) {
  if (!disabled.value && (event.key === "Enter" || event.key === " ")) {
    event.preventDefault();
    handleClick();
  }
}

const skeletonClasses = computed(() => {
  return skeletonTabItemCva({
    tile: tile?.value,
    orientation: orientation?.value,
  });
});

const itemClasses = computed(() => {
  if (isHeader.value) {
    return headerTabItemCva({
      active: activeIndex.value === index.value,
      error: error.value,
      disabled: disabled.value,
      tone: props.tone,
    });
  }

  return [
    "focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500",
    props.muted && activeIndex.value !== index.value && "opacity-45",
    tabItemCva({
      tile: tile?.value,
      orientation: orientation?.value,
      active: activeIndex.value === index.value,
      error: error.value,
      disabled: disabled.value,
      listPosition: listPosition?.value,
      rounded: rounded.value,
      tone: props.tone,
    }),
  ];
});

const iconClasses = computed(() => {
  return tabIconCva({
    tile: tile?.value,
    active: activeIndex.value === index.value,
    error: error.value,
    disabled: disabled.value,
    tone: props.tone,
  });
});

const hasBadge = computed(() => props.badge !== undefined && props.badge !== "" && Boolean(tile?.value));

const hasCount = computed(() => props.count !== undefined && props.count !== "" && !tile?.value && !props.locked);
const showLock = computed(() => props.locked && !tile?.value);

const countClasses = computed(() => tabCountCva({ active: activeIndex.value === index.value }));

const titleClasses = computed(() => {
  // A 16px line under the 17px icon keeps the header bar's tab at the board's 37px.
  if (isHeader.value) {
    return "max-w-full leading-4";
  }

  return tabTitleCva({
    tile: tile?.value,
    orientation: orientation?.value,
  });
});

// Opt-in widening for vertical tile tabs: an inline min/max-width overrides the fixed 100px column
// baked into the tile CVA, so callers with long labels can fit them without touching the shared
// default. Only vertical tiles are affected; horizontal tabs keep their content-driven width.
const verticalTileStyle = computed(() => {
  if (!tile?.value || orientation?.value !== "vertical" || verticalTileWidth?.value == null) {
    return undefined;
  }

  const width = `${verticalTileWidth.value}px`;
  return { minWidth: width, maxWidth: width };
});
</script>

<template>
  <template v-if="props.skeleton">
    <div
      :class="[skeletonClasses]"
      :style="verticalTileStyle"
    >
      <template v-if="tile">
        <VSkeleton
          :width="32"
          width-unit="px"
          :dynamic-width="false"
          :height="32"
          :pill="false"
          class="w-8 h-8 p-1.5 rounded-lg bg-gray-100 dark:bg-dark-700"
        />
        <VSkeleton
          :width="80"
          width-unit="px"
          :dynamic-width="false"
          :height="20"
          :pill="true"
        />
      </template>
      <template v-else>
        <VSkeleton
          :width="24"
          width-unit="px"
          :dynamic-width="false"
          :height="24"
          :pill="false"
          class="rounded-lg bg-gray-100 dark:bg-dark-700"
        />
        <VSkeleton
          :width="orientation === 'vertical' ? 160 : 80"
          width-unit="px"
          :dynamic-width="false"
          :height="20"
          :pill="true"
        />
      </template>
    </div>
  </template>
  <button
    v-else-if="isBar"
    ref="barButton"
    type="button"
    role="tab"
    class="tab-item relative flex flex-1 min-w-16 h-16 flex-col items-center justify-center gap-1 px-1 transition-colors"
    :class="[
      activeIndex === index ? 'text-primary-600 dark:text-primary-400' : 'text-gray-500 dark:text-gray-400',
      disabled && 'opacity-40',
      barOverflow.has(index) && 'hidden',
    ]"
    :aria-selected="activeIndex === index"
    :aria-disabled="disabled"
    :tabindex="disabled ? -1 : 0"
    @click="handleClick"
    @keydown="handleKeyDown"
  >
    <span
      class="relative flex items-center justify-center h-7 w-12 rounded-full transition-colors duration-200"
      :class="activeIndex === index && 'bg-primary-50 dark:bg-primary-900/30'"
    >
      <VIcon v-if="props.icon" :name="props.icon" class="text-[17px]" />
      <span v-if="error" class="absolute right-2 top-0.5 w-2 h-2 rounded-full bg-red-500 ring-2 ring-white dark:ring-dark-800" />
    </span>
    <span class="max-w-full truncate text-[11px] leading-none" :class="activeIndex === index && 'font-semibold'">
      {{ props.title }}
    </span>
  </button>
  <VTooltip
    v-else
    theme="verbleif-tooltip-solid"
    :content="props.tooltip"
    :placement="props.tooltipPosition"
    :disabled="!props.tooltip"
  >
    <template #reference>
      <div
        :class="itemClasses"
        :style="verticalTileStyle"
        role="tab"
        :tabindex="disabled ? -1 : 0"
        :aria-selected="activeIndex === index"
        :aria-disabled="disabled"
        @click="handleClick"
        @keydown="handleKeyDown"
      >
        <div
          v-if="props.icon"
          class="relative"
          :class="[iconClasses]"
        >
          <VIcon :name="props.icon" :class="isHeader ? 'text-[17px]' : 'text-base'" aria-hidden="true" />
          <span
            v-if="hasBadge"
            class="absolute -top-1.5 -right-2.5 h-[18px] min-w-[18px] rounded-full bg-primary-600 px-[5px] text-center text-[11px] leading-[18px] font-medium text-white ring-2 ring-white dark:ring-dark-800"
            data-testid="tab-item-badge"
          >
            {{ props.badge }}
          </span>
        </div>
        <span :class="[titleClasses]">
          {{ props.title }}
        </span>
        <span v-if="hasCount" :class="[countClasses]" data-testid="tab-item-count">
          {{ props.count }}
        </span>
        <VIcon
          v-else-if="showLock"
          name="lock"
          class="ml-1 shrink-0 text-xs text-gray-400 dark:text-gray-500"
          aria-hidden="true"
          data-testid="tab-item-lock"
        />
      </div>
    </template>
  </VTooltip>
</template>
